import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  UserCheck,
  MapPin,
  Settings,
  Shield,
  Briefcase,
  Calendar,
  FileText,
  Bell,
  LogOut,
  Menu,
  X,
  TrendingUp,
  MessageSquare,
  Database,
  Globe,
  Lock,
  UserCog,
  BarChart3,
  Search,
  Filter,
  Download,
  Plus,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Star,
  Clock,
  DollarSign,
  HelpCircle,
  Zap,
  Sun,
  Moon,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import NotificationDropdown from "./NotificationDropdown";

interface AdminLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({
  children,
  title = "Dashboard",
  subtitle,
}) => {
  // Start with sidebar open by default
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
    // Add theme switching logic here if needed
  };

  // Sidebar toggle handler
  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const sidebarSections = [
    {
      title: "MAIN",
      items: [
        { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
        { icon: Briefcase, label: "Categories", path: "/admin/categories" },
        { icon: Users, label: "Customers", path: "/customers" },
        { icon: Briefcase, label: "Services", path: "/services" },
        { icon: UserCheck, label: "Service Providers", path: "/talent" },
        { icon: DollarSign, label: "Memberships", path: "/memberships" },
        { icon: Zap, label: "Subscription Plans", path: "/admin/subscription-plans" },

        {
          icon: MessageSquare,
          label: "Service Inquiries",
          path: "/admin/inquiries",
        },
        { icon: Star, label: "Reviews & Ratings", path: "/reviews" },
        { icon: Bell, label: "Notifications", path: "/notifications" },
        // { icon: FileText, label: 'Content Management', path: '/cms' },
        { icon: Settings, label: "Settings", path: "/settings" },

        // { icon: BarChart3, label: 'Analytics', path: '/analytics' },
        // { icon: TrendingUp, label: 'Reports', path: '/reports' },
      ],
    },
  ];

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <div className={`admin-sidebar ${!sidebarOpen ? "hide" : ""}`}>
        <div className="sidebar-header">
          <Link to="/dashboard" className="sidebar-brand">
            <Zap size={28} />
            SkillSeek
          </Link>
        </div>

        <div className="sidebar-nav">
          {sidebarSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="nav-section">
              <div className="nav-section-title">{section.title}</div>
              {section.items.map((item, itemIndex) => (
                <Link
                  key={itemIndex}
                  to={item.path}
                  className={`nav-item ${isActiveRoute(item.path) ? "active" : ""}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="nav-item-icon" size={20} />
                  {item.label}
                </Link>
              ))}
            </div>
          ))}
        </div>

        {/* Theme Toggle and Logout Section */}
        <div className="sidebar-footer">
          {/* <button 
            onClick={toggleTheme}
            className="theme-toggle"
            title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
          >
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            <span>{isDarkMode ? "Light Mode" : "Dark Mode"}</span>
          </button> */}
          <button
            onClick={handleLogout}
            className="logout-button"
            title="Logout"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            display: window.innerWidth < 768 ? "block" : "none",
          }}
        />
      )}

      {/* Main Content */}
      <div className="admin-main">
        {/* Header */}
        <div className="admin-header">
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <button
              onClick={(e) => {
                handleSidebarToggle();
              }}
              // onMouseDown={(e) => e.preventDefault()}
              className="btn-secondary"
              style={{
                display: "flex",
                alignItems: "center",
                padding: "10px",
                minWidth: "auto",
                zIndex: 1001,
                position: "relative",
                outline: "none", // ← focus ring nahi
              }}
              title="Toggle Sidebar"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}{" "}
              {/* ← sahi icons */}
            </button>
            <div>
              <h1 style={{ fontSize: "24px", fontWeight: "600", margin: 0 }}>
                {title}
              </h1>
              {subtitle && (
                <p
                  style={{
                    color: "var(--text-medium)",
                    margin: 0,
                    fontSize: "14px",
                  }}
                >
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "15px",
            }}
          >
            {/* Search */}
            {/* <div style={{ position: "relative" }}> */}
              {/* <input
                type="text"
                placeholder="Search..."
                className="form-control"
                style={{
                  paddingLeft: "40px",
                  width: "250px",
                  height: "40px",
                }}
              />
              <Search
                size={18}
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--text-medium)",
                }}
              />
            </div> */}

            {/* Notifications */}
            <NotificationDropdown onMarkAllRead={() => {}} />

            {/* User Menu */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  background: "var(--accent-blue)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontWeight: "600",
                }}
              >
                {admin?.name ? admin.name.charAt(0).toUpperCase() : "A"}
              </div>
              <div>
                <div style={{ fontSize: "14px", fontWeight: "500" }}>
                  {admin?.name || "Admin User"}
                </div>
                <div style={{ fontSize: "12px", color: "var(--text-medium)" }}>
                  {admin?.role || "Administrator"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="admin-content">{children}</div>
      </div>
    </div>
  );
};

export default AdminLayout;
